import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import GameModal from "./GameModal";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";

export type GameType = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  // If we want to add a category property, we can define it here
  // But for now, let's keep the interface as is
};

interface GameCardProps {
  game: GameType;
}

const GameCard: React.FC<GameCardProps> = ({ game }) => {
  const isMobile = useIsMobile();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  
  // All games are now available
  const availableGames = [
    "memory-match", "simon-says", "quick-math", "spot-difference", 
    "word-search", "stroop-test", "tower-hanoi", "whack-a-mole",
    "number-sequences", "anagram-solver", "maze-solver", "reaction-test",
    "pattern-memory", "n-back", "word-pairs", "mental-rotation",
    "crossword", "sliding-puzzle", "sudoku", "nonograms",
    "word-chain", "dual-n-back", "task-switching", "sequence-recall"
  ];
  const isGameAvailable = availableGames.includes(game.id);

  return (
    <>
      <motion.div
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        whileTap={{ scale: 0.98 }}
        className="h-full"
        onHoverStart={() => setIsHovering(true)}
        onHoverEnd={() => setIsHovering(false)}
      >
        <Card className="game-card-gradient border border-brain-light-purple/30 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-brain-purple/10 h-full relative">
          {/* Animated card background */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-br from-brain-purple/5 to-brain-teal/5 opacity-0 pointer-events-none"
            animate={{ opacity: isHovering ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
          
          {/* Animated card border glow */}
          <motion.div 
            className="absolute -inset-0.5 bg-gradient-to-br from-brain-purple/30 to-brain-teal/30 opacity-0 blur-sm rounded-xl pointer-events-none"
            animate={{ opacity: isHovering ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
          
          <CardContent className="p-2 sm:p-3 md:p-4 flex flex-col items-center text-center h-full relative z-10">
            <motion.div 
              className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-brain-purple/10 flex items-center justify-center mb-1 md:mb-2 relative overflow-hidden"
              whileHover={{ 
                scale: 1.1, 
                backgroundColor: "rgba(155, 135, 245, 0.2)",
                transition: { duration: 0.2 } 
              }}
            >
              {/* Icon pulse animation */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-br from-brain-purple/20 to-brain-teal/20 opacity-0"
                animate={{ 
                  opacity: isHovering ? [0, 1, 0] : 0,
                  scale: isHovering ? [0.8, 1.2, 0.8] : 1
                }}
                transition={{ 
                  duration: 2,
                  repeat: isHovering ? Infinity : 0,
                  repeatType: "loop"
                }}
              />
              
              <motion.div 
                className="text-brain-purple relative z-10"
                animate={{
                  scale: isHovering ? [1, 1.1, 1] : 1
                }}
                transition={{
                  duration: 1.5,
                  repeat: isHovering ? Infinity : 0,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              >
                {game.icon}
              </motion.div>
            </motion.div>
            
            <motion.h3 
              className="text-xs sm:text-sm md:text-base font-bold mb-0.5 md:mb-1"
              animate={{ 
                color: isHovering ? "#8B5CF6" : "#000000"
              }}
              transition={{ duration: 0.2 }}
            >
              {game.name}
            </motion.h3>
            
            <motion.p 
              className="text-muted-foreground mb-2 md:mb-3 text-xs line-clamp-2"
              animate={{ opacity: isHovering ? 1 : 0.8 }}
              transition={{ duration: 0.3 }}
            >
              {game.description}
            </motion.p>
            
            {isGameAvailable ? (
              <Link to={`/games/${game.id}`} className="mt-auto w-full" aria-label={`Play ${game.name}`}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    className="bg-brain-purple hover:bg-brain-vivid-purple text-white mt-auto text-xs w-full px-1 py-1.5 h-auto relative overflow-hidden"
                    size={isMobile ? "sm" : "default"}
                  >
                    {/* Button shine effect */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-45 opacity-0"
                      animate={{ 
                        opacity: isHovering ? 1 : 0, 
                        left: isHovering ? ["100%", "-100%"] : "100%" 
                      }}
                      transition={{ 
                        duration: 1.5,
                        repeat: isHovering ? Infinity : 0,
                        repeatType: "loop",
                        ease: "easeInOut",
                        delay: 0.2
                      }}
                    />
                    <span className="relative z-10">Play Now</span>
                  </Button>
                </motion.div>
              </Link>
            ) : (
              <Button 
                className="bg-gray-400 text-white mt-auto text-xs w-full px-1 py-1.5 h-auto cursor-default"
                size={isMobile ? "sm" : "default"}
                disabled
              >
                Coming Soon
              </Button>
            )}
          </CardContent>
        </Card>
      </motion.div>

      <GameModal 
        game={game} 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};

export default GameCard;
